import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useWatch, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import FormActions from "../../store/actions/FormActions";
import useCheckboxList from "./useCheckboxList";
import { generalFormSchema } from "../schemas/generalSchema";
import FormMiddlewares from "../../store/middlewares/FormMiddlewares";

export const tabs = [
  {
    text: "tabs.generalTab",
    id: "podcast-info",
  },
  {
    text: "tabs.episodeTab",
    id: "new-episode",
  },
  {
    text: "tabs.episodeListTab",
    id: "episodes",
  },
];

const useEpisodeForm = () => {
  const dispatch = useDispatch();

  const [currentTab, setCurrentTab] = useState(tabs[0].id);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [validateClicked, setValidateClicked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showHighlight, setShowHighlight] = useState(true);

  const checkboxList = useCheckboxList();

  const user = useSelector((state) => state.user);
  const { started } = useSelector((state) => state.form.resetForm);
  const { started: loadFromBrowserStarted } = useSelector(
    (state) => state.form.loadFromBrowser
  );
  const {
    started: uploadFileStarted,
    formValues: uploadFormValues,
    fetching: uploadFileInitiated,
  } = useSelector((state) => state.form.uploadFile);

  const form = useForm({
    resolver: yupResolver(generalFormSchema),
  });
  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    setValue,
    getValues,
    clearErrors,
    reset,
    setFocus,
  } = form;

  const {
    fields: episodes,
    prepend,
    remove,
  } = useFieldArray({
    control,
    name: "episodes",
    keyName: "episodeId",
  });

  const allWatched = useWatch({ control });
  const {
    episodes: latestEpisodes,
    title: latestTitle,
    image_url: latestImageUrl,
    url: latestUrl,
  } = allWatched;

  useEffect(() => {
    if(validateClicked){
      if(isValid){
        setShowHighlight(true);
      }else{
        setShowHighlight(false);
      }
    }

  }, [isValid])

  useEffect(() => {
    dispatch(
      FormActions.syncForm(
        {
          ...{
            ...allWatched,
            episodes: episodes.map((ep, i) => ({
              ...latestEpisodes?.[i],
              previousGuid: ep.previousGuid || ep.episodeId,
            })),
          },
          activeCheckboxes: checkboxList.activeCheckboxes,
          activeChildrens: checkboxList.activeChildrens,
        },
        isValid
      )
    );
  }, [
    isValid,
    allWatched,
    checkboxList.activeChildrens,
    checkboxList.activeCheckboxes,
  ]);

  useEffect(() => {
    if (started) {
      setActiveEpisode(null);
      reset();
      clearErrors();
      setValidateClicked();
      setShowHighlight(true);
      dispatch(FormActions.resetFormComplete());
    }
  }, [started]);

  useEffect(() => {
    if (loadFromBrowserStarted) {
      let loadedForm = localStorage.getItem("ituneXml");
      if (loadedForm) {
        loadedForm = JSON.parse(loadedForm);
        const { activeCheckboxes, activeChildrens, fileName, ...moreForm } =
          loadedForm;

        checkboxList.replaceAll(activeCheckboxes, activeChildrens);

        dispatch(FormActions.loadFromBrowserComplete());
        if (fileName) {
          dispatch(FormActions.setFileName(fileName));
        }

        setActiveEpisode(null);
        clearErrors();
        setValidateClicked();
        Object.keys(moreForm).forEach((key) => {
          setValue(key, moreForm[key]);
        });
      }
    }
  }, [loadFromBrowserStarted]);

  useEffect(() => {
    if (uploadFileStarted) {
      console.log("aaaa we are here");
      const values = uploadFormValues;
      setShowHighlight(false);
      setActiveEpisode(null);
      clearErrors();
      setValidateClicked();
      Object.keys(values).forEach((val) => {
        setValue(val, values[val], {
          shouldValidate: true,
        });
      });

      checkboxList.replaceAll(
        values?.categories?.activeCheckboxes,
        values?.categories?.activeChildrens
      );

      dispatch(FormActions.uploadFileComplete());

      setTimeout(() => {
        dispatch(FormMiddlewares.savePodcast());
      }, 1000);
    }
  }, [uploadFileStarted]);

  useEffect(() => {
    if (uploadFileInitiated) {
      reset();
    }
  }, [uploadFileInitiated]);

  useEffect(() => {
    let loadedForm = localStorage.getItem("ituneXml");
    if (loadedForm) {
      loadedForm = JSON.parse(loadedForm);
      dispatch(FormActions.fetchedFromBrowser());
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(FormMiddlewares.savePodcast());
    }, 10000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const func = async () => {
      const podcast = await dispatch(FormMiddlewares.getMyPodcast());
      if (!podcast) return;

      const {
        podcast: { checkboxes, childrens, ...moreForm },
        settings,
      } = podcast;

      checkboxList.replaceAll(checkboxes, childrens);
      Object.keys(moreForm).forEach((key) => {
        setValue(key, moreForm[key]);
      });

      if (settings.fileName) {
        dispatch(FormActions.setFileName(settings.fileName));
      }
    };
    func();
  }, [user?.isLoggedin]);

  return {
    currentTab,
    setCurrentTab,
    activeEpisode,
    setActiveEpisode,
    validateClicked,
    setValidateClicked,
    modalOpen,
    setModalOpen,
    checkboxList,
    form,
    errors,
    isValid,
    handleSubmit,
    setValue,
    setFocus,
    episodes: episodes.map((ep, i) => ({
      ...ep,
      ...latestEpisodes?.[i],
    })),
    prepend,
    remove,
    latestEpisodes,
    latestTitle,
    latestImageUrl,
    latestUrl,
    showHighlight,
    setShowHighlight
  };
};

export default useEpisodeForm;
